/**
 * useNavVisibility
 *
 * Single source of truth for "which sidebar nav items can this user see".
 *
 * Both the sidebar (AppSidebar) and the post-login landing redirect (HomeRoute
 * in App.tsx) must agree on visibility. When they drift, the "/" redirect lands
 * the user on a route that isn't even the first item in their sidebar — which
 * looked random across roles (e.g. /ideate or /resources/docs). Keeping the
 * predicate here guarantees the landing route is exactly the first item the
 * sidebar renders.
 *
 * Visibility mirrors AppSidebar exactly:
 *  - platformAdmin items require the platform-admin role
 *  - while access is loading, everything is hidden (deny-while-loading)
 *  - adminRole items require an admin-named role (or platform admin)
 *  - featureFlag gates: enforceFeatureFlag uses the REAL tenant flag value,
 *    everything else uses the (kill-switch) isFlagEnabled
 *  - docs is RBAC-exempt (visible whenever its flag passes)
 *  - subFeatureId reads the child eye-toggle (e.g. ontology.migrations.view_actions)
 */

import { useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import { useAssumedRole } from '../contexts/AssumedRoleContext'
import { useFeatureFlags } from '../contexts/FeatureFlagsContext'
import { useCanAccess } from './useCanAccess'
import { isFlagEffectivelyEnabled } from '../constants/featureFlags'
import { NAV_SECTIONS, type NavItem, type NavSection } from '../constants/navigation'

export interface UseNavVisibilityReturn {
  /** True while access checks are still loading — callers should not act on visibility yet. */
  isLoading: boolean
  /** Predicate: does this user see `item` in their sidebar? */
  isItemVisible: (item: NavItem) => boolean
  /**
   * First nav item (in sidebar order) the user can see, scanning the given
   * sections (defaults to the platform NAV_SECTIONS). Returns null if none.
   */
  firstAccessiblePath: (sections?: NavSection[]) => string | null
}

export function useNavVisibility(): UseNavVisibilityReturn {
  const { currentUser } = useAuth()
  const { isPlatformAdmin: isPlatformAdminFromContext } = useTenant()
  const { assumedRole } = useAssumedRole()
  const { canNavigateTo, canViewUI, isFlagEnabled, isLoading } = useCanAccess()
  const { flags } = useFeatureFlags()

  // Switch Role preview: evaluate the assumed role only, so an admin "viewing as
  // developer" sees what that role would see on a real login.
  const isPlatformAdmin = useMemo(() => {
    if (assumedRole) {
      const name = assumedRole.name.toLowerCase().replace(/[\s-]/g, '_')
      return name === 'platform_admin' || name === 'platformadmin'
    }
    if (isPlatformAdminFromContext) return true
    return (
      currentUser?.roles?.some(
        (role) =>
          role.name.toLowerCase() === 'platform_admin' ||
          role.name.toLowerCase() === 'platformadmin'
      ) ?? false
    )
  }, [assumedRole, isPlatformAdminFromContext, currentUser?.roles])

  const hasAdminRole = useMemo(() => {
    if (isPlatformAdmin) return true
    if (assumedRole) {
      const name = assumedRole.name.toLowerCase().replace(/[\s-]/g, '_')
      return name === 'admin' || name === 'platform_admin'
    }
    return (currentUser?.roles ?? []).some((role) => {
      const name = (role?.name ?? '').toLowerCase().replace(/[\s-]/g, '_')
      return name === 'admin' || name === 'platform_admin'
    })
  }, [isPlatformAdmin, assumedRole, currentUser?.roles])

  const isItemVisible = useCallback(
    (item: NavItem): boolean => {
      // Platform admin items require the role AND the normal flag/RBAC gates below.
      if (item.platformAdmin && !isPlatformAdmin) return false

      // While loading, hide everything to avoid acting on partial access data.
      if (isLoading) return false

      // Admin items require an admin-named role on top of the feature-flag gate.
      if (item.adminRole && !hasAdminRole) return false

      if (item.featureFlag) {
        const flagEnabled = item.enforceFeatureFlag
          ? isFlagEffectivelyEnabled(item.featureFlag, flags)
          : isFlagEnabled(item.featureFlag)
        if (!flagEnabled) return false
      }

      if (item.featureId) {
        // Docs is RBAC-exempt: visible to every authenticated user once its flag passes.
        if (item.featureId === 'docs') return true
        return item.subFeatureId
          ? canViewUI(item.featureId, item.subFeatureId)
          : canNavigateTo(item.featureId)
      }

      return true
    },
    [isPlatformAdmin, hasAdminRole, isLoading, isFlagEnabled, flags, canNavigateTo, canViewUI]
  )

  const firstAccessiblePath = useCallback(
    (sections: NavSection[] = NAV_SECTIONS): string | null => {
      for (const section of sections) {
        for (const item of section.items) {
          // The "/" item is Home itself — never a meaningful fallback landing.
          if (item.path === '/' || item.featureId === 'home') continue
          if (isItemVisible(item)) return item.path
        }
      }
      return null
    },
    [isItemVisible]
  )

  return { isLoading, isItemVisible, firstAccessiblePath }
}

export default useNavVisibility
